import React, {useEffect, useRef, useState, useCallback} from 'react';
import OpenSeadragon from 'openseadragon';
import {Extent} from 'ol/extent';
import settings from '../../config/settings';

export interface TileImageMeta {
    dzi: string;
    width: number;
    height: number;
    box: number;
    depth_from: number;
    depth_to: number;
}

export interface TileLayerMeta {
    tiles_path: string;
    images: TileImageMeta[];
}

export interface TileMetadata {
    name: string;
    rgb: TileLayerMeta;
    minerals: Record<string, TileLayerMeta>;
}

export type TileViewSyncState = {
    centerDepth: number;    // meters, positive downward — depth-based for cross-drillhole sync
    centerX: number;        // 0–1 viewport horizontal
    metersPerPixel: number; // real-world meters per screen pixel
    sourceId?: string;
};

export type TileViewerHandle = {
    panBy: (deltaX: number, deltaY: number) => void;
    getCanvasDataUrl: () => string | null;
    // Discard cached tiles and rebuild from scratch (force=true).
    regenerateTiles: () => void;
};

// Lightweight pub/sub for view sync without React re-renders
export class ViewSyncBus {
    private listeners = new Set<(view: TileViewSyncState) => void>();
    current: TileViewSyncState | null = null;

    subscribe(fn: (view: TileViewSyncState) => void) {
        this.listeners.add(fn);
        return () => {
            this.listeners.delete(fn);
        };
    }

    publish(view: TileViewSyncState) {
        this.current = view;
        this.listeners.forEach(fn => fn(view));
    }
}

type Props = {
    name: string;
    projectId: string;
    style?: React.CSSProperties;
    setExtents: (extents: Extent | null) => void;
    syncView?: TileViewSyncState | null;
    onViewChange?: (view: TileViewSyncState) => void;
    viewSyncBus?: ViewSyncBus;
    onReady?: (handle: TileViewerHandle) => void;
    activeLayer?: string;
    onTilesGenerated?: () => void;
};

interface ImageLayout {
    y: number;
    height: number;
    depth_from: number;
    depth_to: number;
}

function getDepthAtViewportY(layouts: ImageLayout[], vpY: number): number {
    return vpY;
}

function getViewportYAtDepth(layouts: ImageLayout[], depth: number): number {
    return depth;
}

function getMetersPerVpUnitAtDepth(layouts: ImageLayout[], depth: number): number {
    return 1;
}

async function fetchTileMetadata(projectId: string, name: string): Promise<TileMetadata | null> {
    try {
        const res = await fetch(`${settings.drillholeApiUrl}/projects/${projectId}/drillholes/${name}/tiles/metadata`);
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}

const TileViewer: React.FC<Props> = ({
                                         name,
                                         projectId,
                                         style,
                                         setExtents,
                                         syncView,
                                         onViewChange,
                                         viewSyncBus,
                                         onReady,
                                         activeLayer = 'rgb',
                                         onTilesGenerated,
                                     }) => {
    const viewerDivRef = useRef<HTMLDivElement | null>(null);
    const viewerRef = useRef<OpenSeadragon.Viewer | null>(null);
    const layoutRef = useRef<ImageLayout[]>([]);
    const isSyncingRef = useRef(false);
    const onViewChangeRef = useRef(onViewChange);
    onViewChangeRef.current = onViewChange;
    const viewSyncBusRef = useRef(viewSyncBus);
    viewSyncBusRef.current = viewSyncBus;
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;
    const setExtentsRef = useRef(setExtents);
    setExtentsRef.current = setExtents;
    const onTilesGeneratedRef = useRef(onTilesGenerated);
    onTilesGeneratedRef.current = onTilesGenerated;
    const rafIdRef = useRef(0);
    const extentsTimeoutRef = useRef(0);
    const userInteractedRef = useRef(false);
    // Timestamp until which we suppress our own emit (covers full animation duration).
    const syncSuppressUntilRef = useRef(0);

    const [metadata, setMetadata] = useState<TileMetadata | null>(null);
    const [metaLoading, setMetaLoading] = useState(true);
    const [metaError, setMetaError] = useState(false);
    const [loading, setLoading] = useState(true);

    // Tile generation state
    const [tileGenStatus, setTileGenStatus] = useState<{
        status: string;
        total: number;
        done: number;
    } | null>(null);
    const tileGenTriggeredRef = useRef(false);
    const pollIntervalRef = useRef<number | null>(null);

    const [mouseCoords, setMouseCoords] = useState<{ x: number; depth: number } | null>(null);
    const lastViewRef = useRef<{ center: OpenSeadragon.Point; zoom: number } | null>(null);
    // Widest tile in viewport units. The horizontal pan is clamped so neither
    // edge can scroll past [0, maxTileVpWidth].
    const maxTileVpWidthRef = useRef(0);
    // Re-entry guard for the horizontal clamp's panTo callback.
    const clampingRef = useRef(false);

    // Fetch metadata from API
    useEffect(() => {
        if (!name || !projectId) return;
        lastViewRef.current = null; // Reset view state when drillhole changes
        setMetaLoading(true);
        setMetaError(false);
        setTileGenStatus(null);
        tileGenTriggeredRef.current = false;
        fetchTileMetadata(projectId, name).then((data) => {
            if (data) {
                setMetadata(data);
            } else {
                setMetaError(true);
            }
            setMetaLoading(false);
        });
    }, [name, projectId]);

    // Start tile generation (optionally forcing a rebuild from scratch) and poll
    // status until completion. Used both for the automatic first-time generation
    // and for the manual "regenerate" button (force=true).
    const startTileGeneration = useCallback((force: boolean) => {
        if (!name || !projectId) return;
        // Avoid stacking multiple poll loops.
        if (pollIntervalRef.current !== null) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }

        const baseUrl = `${settings.drillholeApiUrl}/projects/${projectId}/drillholes/${name}/tiles`;
        const startUrl = force ? `${baseUrl}?force=true` : baseUrl;

        setTileGenStatus({status: 'running', total: 0, done: 0});

        // Start tile generation
        fetch(startUrl, {method: 'POST'})
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (data) setTileGenStatus(data);
            })
            .catch(() => {});

        // Poll every 3 seconds (plain POST returns current status without rebuilding)
        pollIntervalRef.current = window.setInterval(() => {
            fetch(baseUrl, {method: 'POST'})
                .then(res => res.ok ? res.json() : null)
                .then(data => {
                    if (!data) return;
                    setTileGenStatus(data);
                    if (data.status === 'completed' || data.status === 'done') {
                        if (pollIntervalRef.current !== null) {
                            clearInterval(pollIntervalRef.current);
                            pollIntervalRef.current = null;
                        }
                        // Re-fetch metadata now that tiles are ready
                        fetchTileMetadata(projectId, name).then((meta) => {
                            if (meta) {
                                setMetadata(meta);
                                setMetaError(false);
                                setTileGenStatus(null);
                                onTilesGeneratedRef.current?.();
                            }
                        });
                    }
                })
                .catch(() => {});
        }, 3000);
    }, [name, projectId]);

    // Keep a stable ref so the imperative handle (built once) always calls the latest version.
    const startTileGenerationRef = useRef(startTileGeneration);
    startTileGenerationRef.current = startTileGeneration;

    // Clear any running poll loop on unmount / drillhole change.
    useEffect(() => () => {
        if (pollIntervalRef.current !== null) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, [name, projectId]);

    // Trigger tile generation automatically when no tiles are available yet.
    useEffect(() => {
        if (!name || !projectId || !metaError || metaLoading || tileGenTriggeredRef.current) return;
        tileGenTriggeredRef.current = true;
        startTileGeneration(false);
    }, [name, projectId, metaError, metaLoading, startTileGeneration]);

    // View state emission — rAF for sync, debounced for extents (ECharts)
    const emitViewState = useCallback(() => {
        if (rafIdRef.current) return; // already scheduled
        rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = 0;
            if (isSyncingRef.current) return;
            if (performance.now() < syncSuppressUntilRef.current) return;
            if (!viewerRef.current) return;
            const viewport = viewerRef.current.viewport;
            const layouts = layoutRef.current;
            if (layouts.length === 0) return;

            // Extents → ECharts: only after real user interaction, debounced
            if (userInteractedRef.current) {
                if (extentsTimeoutRef.current) clearTimeout(extentsTimeoutRef.current);
                extentsTimeoutRef.current = window.setTimeout(() => {
                    extentsTimeoutRef.current = 0;
                    if (!viewerRef.current) return;
                    const b = viewerRef.current.viewport.getBounds(true);
                    const l = layoutRef.current;
                    if (l.length === 0) return;
                    const depthTop = getDepthAtViewportY(l, b.y);
                    const depthBottom = getDepthAtViewportY(l, b.y + b.height);

                    // console.log('Extents update:', {depthTop, depthBottom, bY: b.y, bH: b.height});
                    setExtentsRef.current([0, -depthBottom, 1, -depthTop]);
                }, 100); // Reduced delay for better responsiveness
            }

            // View sync: depth-based so all drillholes stay at the same real-world depth
            const center = viewport.getCenter();
            const zoom = viewport.getZoom();
            const containerWidth = viewport.getContainerSize().x;

            const centerDepth = getDepthAtViewportY(layouts, center.y);
            const metersPerVpUnit = getMetersPerVpUnitAtDepth(layouts, centerDepth);

            // Use depth-based scale so all drillholes sync to the same meters-per-pixel
            // regardless of tile aspect ratio or horizontal core width.
            // metersPerVpUnit is already computed above (depth m per viewport unit).
            const metersPerPixel = containerWidth > 0 && zoom > 0
                ? metersPerVpUnit / (zoom * containerWidth)
                : 1;

            const viewState: TileViewSyncState = {
                centerDepth,
                centerX: center.x,
                metersPerPixel,
                sourceId: name,
            };

            if (viewSyncBusRef.current) {
                viewSyncBusRef.current.publish(viewState);
            }
            if (onViewChangeRef.current) {
                onViewChangeRef.current(viewState);
            }
        });
    }, [name]);

    // Build the viewer once
    useEffect(() => {
        if (!viewerDivRef.current) return;
        if (viewerRef.current) return;

        const viewer = OpenSeadragon({
            element: viewerDivRef.current,
            prefixUrl: 'https://cdnjs.cloudflare.com/ajax/libs/openseadragon/4.1.1/images/',
            drawer: 'canvas' as any,
            showNavigator: false,
            crossOriginPolicy: 'Anonymous' as any,
            gestureSettingsMouse: {scrollToZoom: true, clickToZoom: false, dblClickToZoom: true},
            gestureSettingsTouch: {pinchToZoom: true},
            minZoomLevel: 0.001,
            maxZoomLevel: 100,
            visibilityRatio: 0,
            constrainDuringPan: false,
            animationTime: 0.15,
            blendTime: 0.05,
            immediateRender: true,
            imageLoaderLimit: 4,
            maxImageCacheCount: 500,
            placeholderFillStyle: '#ffffff',
            subPixelRoundingForTransparency: OpenSeadragon.SUBPIXEL_ROUNDING_OCCURRENCES.ALWAYS as any,
            showZoomControl: false,
            showHomeControl: false,
            showFullPageControl: false,
            showRotationControl: false,
        });

        viewerRef.current = viewer;
        userInteractedRef.current = false;

        const markInteracted = () => {
            userInteractedRef.current = true;
            syncSuppressUntilRef.current = 0;
        };
        viewer.addHandler('canvas-scroll', markInteracted);
        viewer.addHandler('canvas-drag', markInteracted);
        viewer.addHandler('canvas-pinch', markInteracted);

        // Clamp horizontal panning to the widest tile so the user can't scroll
        // past the left or right edge. Vertical pan remains unconstrained.
        const clampHorizontal = () => {
            if (clampingRef.current) return;
            const max = maxTileVpWidthRef.current;
            if (!viewerRef.current || max <= 0) return;
            const vp = viewerRef.current.viewport;
            const bounds = vp.getBounds();
            const halfW = bounds.width / 2;
            const center = vp.getCenter();
            let targetX = center.x;
            if (bounds.width >= max) {
                // Zoomed out further than the tile is wide → center horizontally.
                targetX = max / 2;
            } else if (bounds.x < 0) {
                targetX = halfW;
            } else if (bounds.x + bounds.width > max) {
                targetX = max - halfW;
            }
            if (Math.abs(targetX - center.x) > 1e-9) {
                clampingRef.current = true;
                vp.panTo(new OpenSeadragon.Point(targetX, center.y), true);
                clampingRef.current = false;
            }
        };
        viewer.addHandler('pan', clampHorizontal);
        viewer.addHandler('zoom', clampHorizontal);

        // Track mouse position on canvas
        const tracker = new OpenSeadragon.MouseTracker({
            element: viewer.canvas,
            moveHandler: (event) => {
                if (!viewerRef.current || layoutRef.current.length === 0) return;
                const vpPoint = viewerRef.current.viewport.pointFromPixel(event.position);
                const depth = getDepthAtViewportY(layoutRef.current, vpPoint.y);
                setMouseCoords({x: vpPoint.x, depth});
            },
            leaveHandler: () => {
                setMouseCoords(null);
            }
        });

        viewer.addHandler('animation', emitViewState);

        if (onReadyRef.current) {
            onReadyRef.current({
                panBy: (deltaX: number, deltaY: number) => {
                    if (!viewerRef.current) return;
                    userInteractedRef.current = true;
                    const vp = viewerRef.current.viewport;
                    const center = vp.getCenter();
                    const zoom = vp.getZoom();
                    const pixelRatio = 1 / (vp.getContainerSize().x * zoom);
                    vp.panTo(new OpenSeadragon.Point(
                        center.x + deltaX * pixelRatio,
                        center.y + deltaY * pixelRatio,
                    ), false);
                },
                regenerateTiles: () => {
                    startTileGenerationRef.current(true);
                },
                getCanvasDataUrl: () => {
                    if (!viewerRef.current) return null;
                    try {
                        const canvas = (viewerRef.current as any).drawer?.canvas as HTMLCanvasElement;
                        if (!canvas) return null;
                        // Draw onto a white-background canvas so transparent areas become white
                        const tmp = document.createElement('canvas');
                        tmp.width = canvas.width;
                        tmp.height = canvas.height;
                        const ctx = tmp.getContext('2d')!;
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, tmp.width, tmp.height);
                        ctx.drawImage(canvas, 0, 0);
                        return tmp.toDataURL('image/jpeg', 0.8);
                    } catch {
                        return null;
                    }
                },
            });
        }

        return () => {
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
            if (extentsTimeoutRef.current) clearTimeout(extentsTimeoutRef.current);
            tracker.destroy();
            viewer.destroy();
            viewerRef.current = null;
        };
    }, [emitViewState]);

    // Load images when metadata or active layer changes
    useEffect(() => {
        if (!viewerRef.current || !metadata) return;

        const viewer = viewerRef.current;

        // Save current view state before clearing if user has interacted or we already have tiles
        if (layoutRef.current.length > 0) {
            lastViewRef.current = {
                center: viewer.viewport.getCenter(),
                zoom: viewer.viewport.getZoom()
            };
        }

        viewer.world.removeAll();
        layoutRef.current = [];
        setLoading(true);

        let layerData: { tiles_path: string; images: TileImageMeta[] } | undefined;
        if (activeLayer === 'rgb') {
            layerData = metadata.rgb;
        } else if (metadata.minerals[activeLayer]) {
            layerData = metadata.minerals[activeLayer];
        }

        if (!layerData || layerData.images.length === 0) {
            setLoading(false);
            return;
        }

        const images = layerData.images;
        const tilesBase = `${settings.drillholeApiUrl}/projects/${projectId}/drillholes/${metadata.name}/tiles/${layerData.tiles_path}`;

        let loadedCount = 0;
        const layouts: ImageLayout[] = [];
        let maxVpWidth = 0;

        images.forEach((img) => {
            const vpHeight = img.depth_to - img.depth_from;
            const y = img.depth_from;
            const aspectRatio = img.height / img.width;
            const vpWidth = vpHeight / aspectRatio;
            if (vpWidth > maxVpWidth) maxVpWidth = vpWidth;

            layouts.push({
                y: y,
                height: vpHeight,
                depth_from: img.depth_from,
                depth_to: img.depth_to,
            });

            viewer.addTiledImage({
                tileSource: `${tilesBase}/${img.dzi}`,
                x: 0,
                y: y,
                width: vpWidth,
                success: () => {
                    loadedCount++;
                    if (loadedCount === images.length) {
                        setLoading(false);
                        layoutRef.current = layouts;
                        maxTileVpWidthRef.current = maxVpWidth;

                        // Limit zoom-out so the total tile height fits the viewport at most
                        const minDepth = layouts[0].depth_from;
                        const maxDepth = layouts[layouts.length - 1].depth_to;
                        const totalContentHeight = maxDepth - minDepth;
                        const containerSize = viewer.viewport.getContainerSize();
                        const viewportAspect = containerSize.y / containerSize.x;
                        (viewer.viewport as any).minZoomLevel = viewportAspect / Math.max(0.1, totalContentHeight);

                        if (lastViewRef.current) {
                            viewer.viewport.panTo(lastViewRef.current.center, true);
                            viewer.viewport.zoomTo(lastViewRef.current.zoom, undefined, true);
                        } else {
                            viewer.viewport.goHome(true);
                        }

                        // Sync chart extents to the home view immediately.
                        // emitViewState is gated on userInteracted, so without this the chart
                        // would show the full data range instead of the visible tile range.
                        const busState = viewSyncBusRef.current?.current;
                        if (busState && busState.sourceId !== name) {
                            // Another viewer is already driving the position — apply its state
                            applySyncRef.current(busState);
                        } else {
                            // No active sync: home view shows exactly the full tile depth range
                            setExtentsRef.current([0, -maxDepth, 1, -minDepth]);
                        }
                    }
                },
                error: () => {
                    loadedCount++;
                    if (loadedCount === images.length) {
                        setLoading(false);
                        layoutRef.current = layouts;
                    }
                },
            });
        });
    }, [metadata, activeLayer, emitViewState]);

    // Shared sync application: depth-based positioning, extrapolates beyond content boundaries
    const applySyncRef = useRef<(view: TileViewSyncState) => void>(null as any);
    applySyncRef.current = (view: TileViewSyncState) => {
        if (!viewerRef.current) return;
        const isSelf = view.sourceId === name;
        if (isSelf && userInteractedRef.current) return;
        if (!view.metersPerPixel || view.metersPerPixel <= 0) return;
        const vp = viewerRef.current.viewport;
        const layouts = layoutRef.current;
        if (layouts.length === 0) return;

        // Map depth → viewport Y for THIS drillhole, extrapolating beyond content
        const targetY = getViewportYAtDepth(layouts, view.centerDepth);
        const targetX = view.centerX;

        // Convert metersPerPixel → zoom for this drillhole's coordinate system
        const metersPerVpUnit = getMetersPerVpUnitAtDepth(layouts, view.centerDepth);
        const containerWidth = vp.getContainerSize().x;
        const targetZoom = containerWidth > 0
            ? metersPerVpUnit / (view.metersPerPixel * containerWidth)
            : vp.getZoom();

        // Suppress re-broadcast for the animation duration
        syncSuppressUntilRef.current = performance.now() + 250;

        const currentCenter = vp.getCenter();
        const currentZoom = vp.getZoom();
        if (Math.abs(currentCenter.x - targetX) < 0.0001
            && Math.abs(currentCenter.y - targetY) < 0.0001
            && Math.abs(currentZoom - targetZoom) < 0.01) return;

        // Temporarily lower minZoomLevel so sync can position correctly even beyond content
        const prevMinZoom = (vp as any).minZoomLevel;
        (vp as any).minZoomLevel = 0.001;

        isSyncingRef.current = true;
        vp.panTo(new OpenSeadragon.Point(targetX, targetY), true);
        vp.zoomTo(targetZoom, undefined, true);
        requestAnimationFrame(() => {
            (vp as any).minZoomLevel = prevMinZoom;
            isSyncingRef.current = false;
        });

        // Update chart extents after animation completes — read actual bounds from the viewport
        // (more reliable than computing from pre-animation targetY/targetZoom).
        if (extentsTimeoutRef.current) clearTimeout(extentsTimeoutRef.current);
        extentsTimeoutRef.current = window.setTimeout(() => {
            extentsTimeoutRef.current = 0;
            if (!viewerRef.current) return;
            const b = viewerRef.current.viewport.getBounds();
            const l = layoutRef.current;
            if (l.length === 0) return;
            const depthTop = getDepthAtViewportY(l, b.y);
            const depthBottom = getDepthAtViewportY(l, b.y + b.height);
            setExtentsRef.current([0, -depthBottom, 1, -depthTop]);
        }, 250);
    };

    // Sync from external view state (prop-based)
    useEffect(() => {
        if (!syncView) return;
        applySyncRef.current(syncView);
    }, [syncView]);

    // Sync from ViewSyncBus — bypasses React state entirely
    useEffect(() => {
        if (!viewSyncBus) return;
        return viewSyncBus.subscribe((view) => {
            applySyncRef.current(view);
        });
    }, [viewSyncBus]);

    // No tiles yet — show generation progress if running
    if (metaError || (!metadata && !metaLoading)) {
        const genProgress = tileGenStatus && tileGenStatus.total > 0
            ? Math.round((tileGenStatus.done / tileGenStatus.total) * 100)
            : null;
        return (
            <div style={{
                position: 'relative',
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: '8px',
                border: '1px solid #888',
                overflow: 'hidden', ...(style || {})
            }}>
                {tileGenStatus ? (
                    <>
                        <span style={{color: '#666', fontSize: '0.9rem'}}>
                            Generating tiles{tileGenStatus.status === 'running' ? '...' : ` (${tileGenStatus.status})`}
                        </span>
                        {genProgress != null && (
                            <div style={{width: '60%', maxWidth: '200px'}}>
                                <div style={{
                                    background: '#e0e0e0',
                                    borderRadius: '4px',
                                    height: '8px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        background: '#4caf50',
                                        height: '100%',
                                        width: `${genProgress}%`,
                                        transition: 'width 0.3s ease'
                                    }}/>
                                </div>
                                <span style={{color: '#888', fontSize: '0.75rem'}}>
                                    {tileGenStatus.done} / {tileGenStatus.total} ({genProgress}%)
                                </span>
                            </div>
                        )}
                    </>
                ) : (
                    <span style={{textAlign: 'center', color: '#666'}}>No tiles available</span>
                )}
            </div>
        );
    }

    return (
        <div 
            className="tile-viewer-container"
            style={{
            position: 'relative',
            height: '100%',
            width: '100%',
            outline: '1px solid #888',
            overflow: 'hidden',
            background: '#fff', ...(style || {})
        }}>
            <div
                ref={viewerDivRef}
                style={{width: '100%', height: '100%'}}
            />
            {mouseCoords && (
                <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    pointerEvents: 'none',
                    zIndex: 20,
                    whiteSpace: 'nowrap'
                }}>
                    X: {mouseCoords.x.toFixed(3)}, Depth: {mouseCoords.depth.toFixed(2)}m
                </div>
            )}
            {tileGenStatus && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    background: 'rgba(255,255,255,0.85)',
                    zIndex: 15
                }}>
                    <span style={{color: '#666', fontSize: '0.9rem'}}>
                        Regenerating tiles{tileGenStatus.status === 'running' ? '...' : ` (${tileGenStatus.status})`}
                    </span>
                    {tileGenStatus.total > 0 && (
                        <div style={{width: '60%', maxWidth: '200px'}}>
                            <div style={{
                                background: '#e0e0e0',
                                borderRadius: '4px',
                                height: '8px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    background: '#4caf50',
                                    height: '100%',
                                    width: `${Math.round((tileGenStatus.done / tileGenStatus.total) * 100)}%`,
                                    transition: 'width 0.3s ease'
                                }}/>
                            </div>
                            <span style={{color: '#888', fontSize: '0.75rem'}}>
                                {tileGenStatus.done} / {tileGenStatus.total} ({Math.round((tileGenStatus.done / tileGenStatus.total) * 100)}%)
                            </span>
                        </div>
                    )}
                </div>
            )}
            {(loading || metaLoading) && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.8)',
                    zIndex: 10
                }}>
                    <i className="pi pi-spin pi-spinner" style={{fontSize: 24}}/>
                </div>
            )}
        </div>
    );
};

export default React.memo(TileViewer);
